import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isNil } from 'lodash';

import EmailCommunicationPopover, {
  isEmailCommunicationDisabled,
} from '~/components/Contacts/ContactCommunications/Email/EmailCommunicationPopover';
import SmsCommunicationPopover, {
  isSmsCommunicationDisabled,
} from '~/components/Contacts/ContactCommunications/SMS/SmsCommunicationPopover';
import { contactPopulate } from '~/components/Contacts/ContactUtils';
import Menu from '~/components/core/Atomic/Menu';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { htmlToTextTemplateParser } from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import { useCms } from '~/components/hooks/useCms';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import HoverActionField from '~/components/HoverActionField';
import { ThreeDotsIcon } from '~/components/icons';
import { MIXPANEL_EVENT_SOURCES } from '~/pocs/mixpanel';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

import { canCopyLink, type DocumentRequest } from '../documentRequestDisplay';
import DocumentRequestRecipientDialog from '../DocumentRequestRecipientDialog';
import { TEXTS } from '../texts';
import useDocumentRequestShareTemplate from '../useDocumentRequestShareTemplate';

interface DocumentRequestLinkActionsProps {
  organizationId: number;
  claimId: number;
  request: DocumentRequest;
  isWriteDisabled: boolean;
  isCancelDisabled: boolean;
  onCancel: () => void;
}

type Composer = 'sms' | 'email';

interface ResendComposerProps {
  organizationId: number;
  claimId: number;
  documentRequestId: number;
  composer: Composer;
  contact: ContactFullModel;
  onClose: () => void;
  onTemplateReady: () => void;
  onTemplateFailed: () => void;
}

// Opens the composer once the organization's message for this link and recipient has arrived. Mounted per
// pick (see its key), so a composer never opens with the message an earlier pick fetched.
const ResendComposer: React.FC<ResendComposerProps> = ({
  organizationId,
  claimId,
  documentRequestId,
  composer,
  contact,
  onClose,
  onTemplateReady,
  onTemplateFailed,
}) => {
  const { data: templateData, isError } = useDocumentRequestShareTemplate(
    { organizationId, claimId, documentRequestId, contactId: contact.id },
    // The menu's snackbar says what failed and what to do instead.
    { shouldOpenErrorDialog: false }
  );
  // The picked contact carries no primary_email (an Advanced Search row is the organization's list row), and the
  // email composer sends to it. Memoized: the composers list the contact in their hook dependencies.
  const recipient = useMemo(() => contactPopulate(contact), [contact]);

  useEffect(() => {
    if (isError) {
      onTemplateFailed();
    }
  }, [isError, onTemplateFailed]);

  useEffect(() => {
    if (!isNil(templateData)) {
      onTemplateReady();
    }
  }, [templateData, onTemplateReady]);

  if (isNil(templateData)) {
    return null;
  }

  return composer === 'sms' ? (
    <SmsCommunicationPopover
      contact={recipient}
      claimId={claimId}
      messageContent={htmlToTextTemplateParser(templateData.sms_html || '', true)}
      mixpanelSource={MIXPANEL_EVENT_SOURCES.DOCUMENT_REQUESTS_RESEND_SMS_CLICKED}
      isOpen
      onClose={onClose}
    />
  ) : (
    <EmailCommunicationPopover
      contact={recipient}
      claimId={claimId}
      emailTitle={htmlToTextTemplateParser(templateData.title_email_html)}
      emailText={templateData.body_email_html}
      mixpanelSource={MIXPANEL_EVENT_SOURCES.DOCUMENT_REQUESTS_RESEND_EMAIL_CLICKED}
      isOpen
      onClose={onClose}
    />
  );
};

// The row menu of an awaiting-upload or uploading link, built as the Documents table's row menu
// (DocumentMoreActionsContainer).
const DocumentRequestLinkActions: React.FC<DocumentRequestLinkActionsProps> = ({
  organizationId,
  claimId,
  request,
  isWriteDisabled,
  isCancelDisabled,
  onCancel,
}) => {
  const documentRequestId = request.id;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // A link stores no recipient, so a resend first asks who to send it to, then opens that composer.
  const [pickingFor, setPickingFor] = useState<Composer | null>(null);
  // Numbered, so that each pick mounts its own composer: an email composer minimized from an earlier pick keeps
  // that pick open, and the email popover launches its composer once per mount.
  const [recipient, setRecipient] = useState<{ composer: Composer; contact: ContactFullModel; pick: number } | null>(
    null
  );
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
  const { user } = useCms();
  const canWrite = useHasPermission({ action: PERMISSION_ACTIONS.CLAIM_DOCUMENT, verb: PERMISSION_VERBS.WRITE });

  const handleCopy = async (copyUrl: string) => {
    setAnchorEl(null);
    try {
      await navigator.clipboard.writeText(copyUrl);
      showSnackbar(TEXTS.COPY_LINK_SUCCESS, 'success');
    } catch (error) {
      showSnackbar(TEXTS.COPY_LINK_FAILED, 'error');
    }
  };

  const openResend = (composer: Composer) => {
    setAnchorEl(null);
    setPickingFor(composer);
  };

  // Memoized: ResendComposer's effect lists it.
  const handleTemplateFailed = useCallback(() => {
    showSnackbar(TEXTS.SHARE_TEMPLATE_FAILED, 'error');
    setRecipient(null);
  }, [showSnackbar]);

  const showCopy = canCopyLink(request);
  const showResend = showCopy && canWrite;
  const showCancel = canWrite;

  if (!showCopy && !showCancel) {
    return null;
  }

  return (
    <>
      <HoverActionField
        icon={ThreeDotsIcon}
        onAction={(event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        permanent
        ignorePermissions
        actionAriaLabel={TEXTS.LINK_ACTIONS_ARIA_LABEL}
      />
      <Menu
        PaperProps={{ style: { width: 200 } }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {showCopy ? <MenuItem onClick={() => handleCopy(request.url)}>{TEXTS.COPY_LINK_MENU_ITEM}</MenuItem> : null}
        {showResend ? (
          <MenuItem disabled={isWriteDisabled} onClick={() => openResend('sms')}>
            {TEXTS.RESEND_SMS_MENU_ITEM}
          </MenuItem>
        ) : null}
        {showResend ? (
          <MenuItem disabled={isWriteDisabled} onClick={() => openResend('email')}>
            {TEXTS.RESEND_EMAIL_MENU_ITEM}
          </MenuItem>
        ) : null}
        {showCancel ? (
          <MenuItem
            disabled={isCancelDisabled}
            onClick={() => {
              setAnchorEl(null);
              onCancel();
            }}
          >
            {TEXTS.CANCEL_LINK_MENU_ITEM}
          </MenuItem>
        ) : null}
      </Menu>
      {isNil(pickingFor) ? null : (
        <DocumentRequestRecipientDialog
          title={pickingFor === 'sms' ? TEXTS.RESEND_SMS_MENU_ITEM : TEXTS.RESEND_EMAIL_MENU_ITEM}
          intro={TEXTS.RESEND_RECIPIENT_INTRO}
          primaryLabel={TEXTS.CONTINUE_BUTTON}
          onClose={() => setPickingFor(null)}
          // A contact the composer could not send to is refused here, with the reason, rather than in the composer.
          getContactBlockedReason={(contact) => {
            if (pickingFor === 'sms') {
              return isSmsCommunicationDisabled(user, contact) ? TEXTS.RESEND_SMS_BLOCKED : undefined;
            }
            return isEmailCommunicationDisabled(user, contact) ? TEXTS.RESEND_EMAIL_BLOCKED : undefined;
          }}
          onSubmit={(contact) => {
            setRecipient((previous) => ({ composer: pickingFor, contact, pick: (previous?.pick ?? 0) + 1 }));
            setPickingFor(null);
            // The picker closes now and the composer opens only once the message arrives.
            showSnackbar(TEXTS.RESEND_PREPARING, 'info');
          }}
        />
      )}
      {/* Mounted only while open: each popover loads the claim when it mounts. */}
      {isNil(recipient) ? null : (
        <ResendComposer
          key={recipient.pick}
          organizationId={organizationId}
          claimId={claimId}
          documentRequestId={documentRequestId}
          composer={recipient.composer}
          contact={recipient.contact}
          onClose={() => setRecipient(null)}
          onTemplateReady={closeSnackbar}
          onTemplateFailed={handleTemplateFailed}
        />
      )}
      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </>
  );
};

export default DocumentRequestLinkActions;
